import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import type { Note } from './types';
import type { LineNote, LineNoteDraft } from './types/line-notes';
import { LineNotesApi } from './api/line-notes-api';
import type { ProjectScope } from '../sync/project-scope';
import { deleteNoteMetadata, saveNoteMetadata } from '../sync/notes-api';
import {
    clearAcknowledgedPendingLineNoteDeletes,
    clearPendingLineNoteDelete,
    clearPendingLineNoteWrites,
    isPendingLineNoteDelete,
    markPendingLineNoteDelete,
} from '../sync/line-note-pending-writes';
import { markPendingNoteDelete, markPendingNoteWrite } from '../sync/note-pending-writes';
import { logger } from '@/utils/logger';
import { showToast } from '@/features/layout/notifications';
import { DEFAULT_NOTE_TYPE, normalizeNoteType } from './note-type';

export interface NoteSlice {
    notes: Note[];
    lineNotes: LineNote[];
    noteActions: {
        addNote: (note: Partial<Note>) => string;
        updateNote: (id: string, updates: Partial<Note>) => void;
        deleteNote: (id: string) => void;
        
        // Line Notes
        addLineNote: (note: LineNoteDraft) => Promise<string>;
        deleteLineNote: (id: string) => Promise<void>;
        updateLineNoteComment: (id: string, comment: string) => Promise<void>;
        loadLineNotes: (date: string) => Promise<void>;
        loadLineNotesForProject: () => Promise<void>;
        
        // Project scoping
        clearNotes: () => void;
    };
}

function getNoteProjectScope(state: AppState & NoteSlice): ProjectScope | null {
    if (!state.cloud.enabled || !state.cloud.orgId || !state.cloud.projectId) {
        return null;
    }
    return { orgId: state.cloud.orgId, projectId: state.cloud.projectId };
}

function isSameProjectScope(left: ProjectScope | null, right: ProjectScope): boolean {
    return left?.orgId === right.orgId && left?.projectId === right.projectId;
}

const NOTE_PRESERVATION_RETRY_DELAYS_MS = [1000, 5000];
const NOTE_METADATA_SAVE_DEBOUNCE_MS = 250;
const pendingNoteMetadataSaveTimers = new Map<string, number>();

type VersionedLineNoteComment = {
    comment: LineNote['comment'];
    version: number;
};

type LineNoteCommentWriteState = {
    settled: VersionedLineNoteComment & { awaitingAcknowledgment: boolean };
    pending: Map<number, LineNote['comment']>;
};

const lineNoteCommentWriteStates = new Map<string, LineNoteCommentWriteState>();
let nextLineNoteCommentOperationId = 0;
const latestLineNoteLoadGenerationByScope = new Map<string, number>();
let nextLineNoteLoadGeneration = 0;

function noteMetadataSaveKey(scope: ProjectScope, noteId: string): string {
    return `${scope.orgId}/${scope.projectId}/${noteId}`;
}

function lineNoteLoadScopeKey(scope: ProjectScope): string {
    return `${scope.orgId}\0${scope.projectId}`;
}

function beginLineNoteLoad(scope: ProjectScope): number {
    const generation = ++nextLineNoteLoadGeneration;
    latestLineNoteLoadGenerationByScope.set(lineNoteLoadScopeKey(scope), generation);
    return generation;
}

function isLatestActiveLineNoteLoad(
    activeScope: ProjectScope | null,
    loadScope: ProjectScope,
    generation: number
): boolean {
    return isSameProjectScope(activeScope, loadScope) &&
        latestLineNoteLoadGenerationByScope.get(lineNoteLoadScopeKey(loadScope)) === generation;
}

function beginLineNoteCommentOperation(
    scope: ProjectScope,
    noteId: string,
    confirmedComment: LineNote['comment'],
    pendingComment: LineNote['comment']
): number {
    const key = noteMetadataSaveKey(scope, noteId);
    const existingState = lineNoteCommentWriteStates.get(key);
    const state = existingState ?? {
            settled: {
                comment: confirmedComment,
                version: 0,
                awaitingAcknowledgment: false,
            },
            pending: new Map<number, LineNote['comment']>(),
        };
    const operationId = ++nextLineNoteCommentOperationId;
    state.pending.set(operationId, pendingComment);
    lineNoteCommentWriteStates.set(key, state);
    return operationId;
}

function getLatestLineNoteComment(writeState: LineNoteCommentWriteState): VersionedLineNoteComment {
    let latest: VersionedLineNoteComment = writeState.settled;
    for (const [operationId, pendingComment] of writeState.pending) {
        if (operationId > latest.version) {
            latest = { comment: pendingComment, version: operationId };
        }
    }
    return latest;
}

function reconcileLineNoteCommentOperation(
    scope: ProjectScope,
    noteId: string,
    operationId: number,
    comment: LineNote['comment'],
    succeeded: boolean
): LineNote['comment'] | null {
    const key = noteMetadataSaveKey(scope, noteId);
    const state = lineNoteCommentWriteStates.get(key);
    if (!state?.pending.delete(operationId)) {
        return null;
    }

    if (succeeded && operationId > state.settled.version) {
        state.settled = {
            comment,
            version: operationId,
            awaitingAcknowledgment: true,
        };
    }

    const latest = getLatestLineNoteComment(state);

    if (state.pending.size === 0 && !state.settled.awaitingAcknowledgment) {
        lineNoteCommentWriteStates.delete(key);
    }

    return latest.comment;
}

function clearPendingNoteMetadataSave(scope: ProjectScope, noteId: string): void {
    const key = noteMetadataSaveKey(scope, noteId);
    const pendingTimer = pendingNoteMetadataSaveTimers.get(key);
    if (pendingTimer !== undefined) {
        window.clearTimeout(pendingTimer);
        pendingNoteMetadataSaveTimers.delete(key);
    }
}

function persistNoteChange(label: string, action: () => Promise<void> | void, attempt = 0): void {
    try {
        const result = action();
        void Promise.resolve(result).catch(error => {
            logger.error(`[notes] Failed to ${label}:`, error);
            const retryDelayMs = NOTE_PRESERVATION_RETRY_DELAYS_MS[attempt];
            if (retryDelayMs !== undefined) {
                window.setTimeout(() => {
                    persistNoteChange(label, action, attempt + 1);
                }, retryDelayMs);
                return;
            }
            showToast('Note changes are still local. Keep this tab open while preservation recovers.', 'error');
        });
    } catch (error) {
        logger.error(`[notes] Failed to ${label}:`, error);
        const retryDelayMs = NOTE_PRESERVATION_RETRY_DELAYS_MS[attempt];
        if (retryDelayMs !== undefined) {
            window.setTimeout(() => {
                persistNoteChange(label, action, attempt + 1);
            }, retryDelayMs);
            return;
        }
        showToast('Note changes are still local. Keep this tab open while preservation recovers.', 'error');
    }
}

function schedulePersistNoteMetadata(scope: ProjectScope, note: Note): void {
    clearPendingNoteMetadataSave(scope, note.id);
    const key = noteMetadataSaveKey(scope, note.id);
    const timer = window.setTimeout(() => {
        pendingNoteMetadataSaveTimers.delete(key);
        persistNoteChange('save note metadata', () => saveNoteMetadata(scope, note));
    }, NOTE_METADATA_SAVE_DEBOUNCE_MS);
    pendingNoteMetadataSaveTimers.set(key, timer);
}

function normalizeNoteUpdates(updates: Partial<Note>): Partial<Note> {
    if (!('type' in updates)) {
        return updates;
    }

    return {
        ...updates,
        type: normalizeNoteType(updates.type),
    };
}

function mergeProjectLineNotes(
    scope: ProjectScope,
    fetchedNotes: LineNote[],
    localNotes: LineNote[]
): LineNote[] {
    const fetchedNoteIds = new Set(fetchedNotes.map(note => note.id));
    clearAcknowledgedPendingLineNoteDeletes(scope, fetchedNoteIds);

    const remoteVisibleNotes = mergePendingLineNoteComments(scope, fetchedNotes);
    const localOnlyNotes = localNotes.filter(note => (
        !fetchedNoteIds.has(note.id) &&
        !isPendingLineNoteDelete(scope, note.id) &&
        !lineNoteCommentWriteStates.has(noteMetadataSaveKey(scope, note.id))
    ));

    return [...remoteVisibleNotes, ...localOnlyNotes];
}

function removePendingDeletedLineNotes(scope: ProjectScope, notes: LineNote[]): LineNote[] {
    return notes.filter(note => !isPendingLineNoteDelete(scope, note.id));
}

function mergePendingLineNoteComments(scope: ProjectScope, fetchedNotes: LineNote[]): LineNote[] {
    return removePendingDeletedLineNotes(scope, fetchedNotes).map(note => {
        const key = noteMetadataSaveKey(scope, note.id);
        const writeState = lineNoteCommentWriteStates.get(key);
        if (!writeState) {
            return note;
        }

        if (
            writeState.settled.awaitingAcknowledgment &&
            note.comment === writeState.settled.comment
        ) {
            writeState.settled.awaitingAcknowledgment = false;
        }
        if (!writeState.settled.awaitingAcknowledgment) {
            writeState.settled.comment = note.comment;
        }

        const latest = getLatestLineNoteComment(writeState);

        if (writeState.pending.size === 0 && !writeState.settled.awaitingAcknowledgment) {
            lineNoteCommentWriteStates.delete(key);
        }

        return latest.version > 0 ? { ...note, comment: latest.comment } : note;
    });
}

export const createNoteSlice: StateCreator<
    AppState & NoteSlice,
    [["zustand/immer", never]],
    [],
    NoteSlice
> = (set, get) => ({
    notes: [],
    lineNotes: [],
    noteActions: {
        addNote: (note) => {
            const id = crypto.randomUUID();
            const timestamp = new Date().toISOString();
            const newNote: Note = {
                id,
                title: note.title || 'Untitled Note',
                content: note.content || '',
                createdAt: timestamp,
                updatedAt: timestamp,
                ...note,
                type: normalizeNoteType(note.type ?? DEFAULT_NOTE_TYPE),
            };
            set(state => {
                if (!state.notes) state.notes = [];
                state.notes.push(newNote);
            });
            const scope = getNoteProjectScope(get());
            if (scope) {
                markPendingNoteWrite(scope, newNote.id);
                schedulePersistNoteMetadata(scope, newNote);
            }
            return id;
        },
        updateNote: (id, updates) => {
            const normalizedUpdates = normalizeNoteUpdates(updates);
            set(state => {
                const note = state.notes.find(n => n.id === id);
                if (note) {
                    Object.assign(note, normalizedUpdates);
                    note.updatedAt = new Date().toISOString();
                }
            });
            const scope = getNoteProjectScope(get());
            const updatedNote = get().notes.find(note => note.id === id);
            if (scope && updatedNote) {
                markPendingNoteWrite(scope, updatedNote.id);
                schedulePersistNoteMetadata(scope, updatedNote);
            }
        },
        deleteNote: (id) => {
            set(state => {
                state.notes = state.notes.filter(n => n.id !== id);
            });
            const scope = getNoteProjectScope(get());
            if (scope) {
                markPendingNoteDelete(scope, id);
                clearPendingNoteMetadataSave(scope, id);
                persistNoteChange('delete note metadata', () => deleteNoteMetadata(scope, id));
            }
        },

        // Line Notes Implementation
        addLineNote: async (note) => {
            const scope = getNoteProjectScope(get());
            if (!scope) throw new Error('No active project');

            const newNote = await LineNotesApi.createLineNote(scope, note);
            
            set(state => {
                if (!state.lineNotes) state.lineNotes = [];
                state.lineNotes.push(newNote);
            });
            
            return newNote.id;
        },

        deleteLineNote: async (id) => {
            const scope = getNoteProjectScope(get());
            if (!scope) return;

            // Optimistic update
            const previousNotes = get().lineNotes;
            markPendingLineNoteDelete(scope, id);
            set(state => {
                state.lineNotes = state.lineNotes.filter(n => n.id !== id);
            });

            try {
                await LineNotesApi.deleteLineNote(scope, id);
            } catch (error) {
                // Rollback on error
                clearPendingLineNoteDelete(scope, id);
                set(state => {
                    state.lineNotes = previousNotes;
                });
                console.error('Failed to delete line note:', error);
                throw error;
            }
        },

        updateLineNoteComment: async (id, comment) => {
            const scope = getNoteProjectScope(get());
            if (!scope) return;

            const trimmedComment = comment.trim();
            if (trimmedComment.length > 1000) {
                throw new Error('Line note comments must be 1,000 characters or fewer.');
            }

            const previousComment = get().lineNotes.find(note => note.id === id)?.comment;
            const operationId = beginLineNoteCommentOperation(scope, id, previousComment, trimmedComment);
            set(state => {
                state.lineNotes = state.lineNotes.map(note => (
                    note.id === id ? { ...note, comment: trimmedComment } : note
                ));
            });

            try {
                await LineNotesApi.updateLineNote(scope, id, { comment: trimmedComment });
                const reconciledComment = reconcileLineNoteCommentOperation(
                    scope,
                    id,
                    operationId,
                    trimmedComment,
                    true
                );
                if (reconciledComment !== null) {
                    set(state => {
                        state.lineNotes = state.lineNotes.map(note => (
                            note.id === id ? { ...note, comment: reconciledComment } : note
                        ));
                    });
                }
            } catch (error) {
                const reconciledComment = reconcileLineNoteCommentOperation(
                    scope,
                    id,
                    operationId,
                    trimmedComment,
                    false
                );
                if (reconciledComment !== null) {
                    set(state => {
                        state.lineNotes = state.lineNotes.map(note => (
                            note.id === id ? { ...note, comment: reconciledComment } : note
                        ));
                    });
                }
                throw error;
            }
        },

        loadLineNotes: async (date) => {
            const scope = getNoteProjectScope(get());
            if (!scope) return;
            const generation = beginLineNoteLoad(scope);

            try {
                const notes = await LineNotesApi.getLineNotesByDate(scope, date);
                if (!isLatestActiveLineNoteLoad(getNoteProjectScope(get()), scope, generation)) {
                    return;
                }
                set(state => {
                    state.lineNotes = mergePendingLineNoteComments(scope, notes);
                });
            } catch (error) {
                console.error('Failed to load line notes:', error);
            }
        },

        loadLineNotesForProject: async () => {
            const scope = getNoteProjectScope(get());
            if (!scope) return;
            const generation = beginLineNoteLoad(scope);

            try {
                const notes = await LineNotesApi.getLineNotes(scope);
                if (!isLatestActiveLineNoteLoad(getNoteProjectScope(get()), scope, generation)) {
                    return;
                }
                set(state => {
                    state.lineNotes = mergeProjectLineNotes(scope, notes, state.lineNotes);
                });
            } catch (error) {
                console.error('Failed to load project line notes:', error);
            }
        },
        
        clearNotes: () => {
            clearPendingLineNoteWrites();
            lineNoteCommentWriteStates.clear();
            latestLineNoteLoadGenerationByScope.clear();
            set(state => {
                state.notes = [];
                state.lineNotes = [];
            });
        }
    }
});
